'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAtom, useAtomValue } from 'jotai';
import { usePrivy } from '@privy-io/react-auth';
import BaseModal from '@/app/components/Modals/BaseModal';
import ModalConnect from './components/ModalConnect';
import { tokenInfoAtom } from '@/app/atoms/token';
import { userDataAtom } from '@/app/atoms/userData';
import { selectedProposalTypeAtom } from '@/app/atoms/proposalForm';
import { crank } from '@/app/helpers/crank';
import ActionButton from './components/ActionButton';
import { useTokenContext } from '@/app/context/TokensContext';
import ProposalRadioButtons from './components/ProposalRadioButtons';
import {
  proposalThresholdAtom,
  circulatingSupplyAtom,
} from '@/app/atoms/governorContract';
import { openTransactionUrl } from '@/app/helpers/openTransactionUrl';
import {
  targetAddressAtom,
  proposalTypeDataAtom,
} from '@/app/karratco/submit/proposal/proposalForm';
import {
  getContractsForProposalType,
  getProposalTypeKey,
} from '@/app/karratco/submit/proposal/aux';

interface ProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProposalModal: React.FC<ProposalModalProps> = ({ isOpen, onClose }) => {
  const tokenInfo = useAtomValue(tokenInfoAtom);
  const { symbol } = tokenInfo;
  const { authenticated } = usePrivy();
  const userData = useAtomValue(userDataAtom);
  const proposalThreshold = useAtomValue(proposalThresholdAtom);

  const [isPending, setIsPending] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [showProposalOptions, setShowProposalOptions] = useState<boolean>(false);
  const [selectedProposalType, setSelectedProposalType] = useAtom(
    selectedProposalTypeAtom,
  );

  const showDelegateSelfButton =
    !userData.isUserSelfDelegated && tokenInfo?.balance !== '0';
  const { tokenDelegate } = useTokenContext();
  let circulatingSupply = useAtomValue(circulatingSupplyAtom);
  circulatingSupply = circulatingSupply!.replace(/,/g, '');

  const router = useRouter();

  const [, setTargetAddress] = useAtom(targetAddressAtom);

  const getButtonText = () => {
    if (isPending) return 'Delegating...';
    if (isConfirming) return 'Processing...';
    if (isConfirmed && txHash) return 'View on BlockExplorer';
    if (isConfirmed) return 'Delegated';
    if (error) return 'Self Delegate';
    return 'Self Delegate';
  };

  const handleButtonClick = async () => {
    setIsPending(true);
    setIsConfirming(false);
    setIsConfirmed(false);
    setError(null);
    try {
      const delegatee = userData.address as `0x${string}`;
      const result = await tokenDelegate({ delegatee });
      setTxHash(result.txHash);
      setIsConfirming(true);
      // Triggering the API data update
      crank();
      setIsConfirmed(true);
    } catch (error) {
      setError('An error occurred during delegation');
    } finally {
      setIsPending(false);
      setIsConfirming(false);
    }
  };

  const handleViewTx = () => {
    if (txHash) {
      openTransactionUrl(txHash);
    }
  };

  const handleSubmit = () => {
    if (selectedProposalType) {
      router.push('/karratco/submit/proposal');
    }
  };

  const handleClose = () => {
    setSelectedProposalType('');
    onClose();
  };

  let title;
  if (authenticated) {
    title = showProposalOptions ? 'Choose Proposal Type' : 'Proposal Threshold Not Met';
  } else {
    title = 'Connect Wallet';
  }

  const options: { [key: string]: { type: string; tooltip: string }[] } = {
    'Constitutional Proposals': [
      {
        type: 'Constitutional Upgrades',
        tooltip:
          "Propose changes to the DAO's operational processes, such as voting mechanisms or governance structures.",
      },
    ],
    'Non-Constitutional Proposals': [
      {
        type: 'Funding Requests',
        tooltip:
          "Request allocation of funds from the DAO's treasury for projects, grants, or operational expenses.",
      },
      {
        type: 'Informational',
        tooltip:
          'Share general information or guidelines with the community that do not require actionable steps or changes to the protocol.',
      },
    ],
  };

  useEffect(() => {
    if (authenticated && userData?.meetsProposalThreshold) {
      setShowProposalOptions(true);
    } else {
      setShowProposalOptions(false);
    }
  }, [authenticated, userData?.meetsProposalThreshold]);

  useEffect(() => {
    if (selectedProposalType) {
      const contracts = getContractsForProposalType(
        getProposalTypeKey(selectedProposalType),
      );

      if (contracts.length > 0) {
        setTargetAddress(contracts[0]);
      } else {
        setTargetAddress(null);
      }
    }
  }, [selectedProposalType, setTargetAddress]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={title!}
      disableCloseButton={isPending || isConfirming}
    >
      {!authenticated ? (
        <div className="min-w-full flex flex-col items-center">
          <ModalConnect />
        </div>
      ) : showProposalOptions ? (
        <>
          <ProposalRadioButtons
            selectedProposalType={selectedProposalType}
            setSelectedProposalType={setSelectedProposalType}
            options={options}
          />
          <button
            className={`mt-4 py-2 px-4 w-full border border-link text-btntext rounded-md bg-link text-sm ${!selectedProposalType ? 'cursor-not-allowed hover:bg-link' : 'cursor-pointer hover:bg-hover hover:text-btntexthover hover:border-hover'}`}
            onClick={handleSubmit}
            disabled={!selectedProposalType}
          >
            Submit
          </button>
        </>
      ) : (
        <div className="text-center">
          <p className="mt-4 mb-6 text-pretty">
            At least {proposalThreshold} {symbol}s must be
            <span className="font-bold italic mx-1">delegated to you</span> before you can
            create a proposal.
          </p>
          <p className="mt-4 mb-6 text-pretty">
            You currently have {userData.votingPower} {symbol}s
            <span className="font-bold italic mx-1">delegated to you</span>
          </p>
          <div className="flex flex-col justify-center space-y-2">
            {showDelegateSelfButton && (
              <ActionButton
                selectedAction={'selfDelegate'}
                isPending={isPending}
                isConfirming={isConfirming}
                buttonTxt={getButtonText()}
                handleSubmit={handleButtonClick}
                handleButtonClick={handleButtonClick}
                handleViewTx={handleViewTx}
                userData={userData}
              />
            )}

            <Link
              href="/karratco/submit/delegationstatement"
              className={`flex justify-center w-full bg-link text-btntext hover:text-btntexthover hover:bg-hover rounded py-2 ${isPending && 'hidden'}`}
            >
              Ask for delegations
            </Link>
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default ProposalModal;
